import ScrollBar from "@components/common/scrollbar";
import { CalendarModeRangeType } from "@custom-types/Types";
import { useLang } from "@hooks/useLang";
import {
  CALENDAR,
  CALENDAR_MODE_RANGE_TYPE,
  FORMAT,
  LANG
} from "@resources/Constants";
import { useAppSelector } from "@configs/Redux";
import { getDateTime, getDateTimeCalendar } from "@utils/Widget";
import { HVButton, HVIcon, HVText } from "@repo/HVComponents";
import moment from "moment";
import momentTimeZone from "moment-timezone";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { IconTimeZone } from "@resources/icons/Icons";
import { Calendar } from "..";
import {
  DateRangePickerProps,
  SearchComparisonDateTimeTemporary
} from "./Interfaces";
import {
  DateRangePickerContainerStyled,
  DateRangePickerStyled,
  ButtonSubmit
} from "./DateRangePicker.styled";
import { ICON } from "../../../../constants/icons";
import "moment/locale/ko";
import "moment/locale/fr";
import "moment/locale/it";
import "moment/locale/de";
import "moment/locale/pt";
import "moment/locale/es";
import { TimeZone } from "../time-zone";

moment.locale(LANG.EN.toLocaleLowerCase());

export const DateRangePicker = <T extends SearchComparisonDateTimeTemporary>({
  modes,
  displayMode = false,
  noTextMode = false,
  showIcon = false,
  dateTime,
  setDateTime,
  tabIndex,
  yearIsLimit,
  showRangeMode,
  showTimePicker,
  dateFormat,
  usePreview = true,
  showDropdownIcon,
  timeZone,
  setTimeZone,
  currentUserTimeZone,
  isConstantTimezone,
  isCalendar,
  ...props
}: DateRangePickerProps<T>) => {
  const uniq = useId();
  const { translate } = useLang();
  const { lang } = useAppSelector((state) => state.system);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(!usePreview);
  const [previousTimeZone, setPreviousTimeZone] = useState<string>("");
  const [selectedDateTime, setSelectedDateTime] =
    useState<SearchComparisonDateTimeTemporary>(dateTime);
  const [selectedTempDateTime, setSelectedTempDateTime] =
    useState<SearchComparisonDateTimeTemporary>(dateTime);
  const [currentTime, setCurrentTime] = useState<string>();
  const [currentTimeZone, setCurrentTimeZone] = useState<string>(
    (timeZone as string) || moment.tz.guess()
  );
  const {
    mode: selectedMode,
    start: selectedStart,
    end: selectedEnd
  } = selectedTempDateTime;
  const intervalCurrentTime = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const [maxDate, setMaxDate] = useState<Date>();

  const [startHour, setStartHour] = useState<number>(12);
  const [startMinute, setStartMinute] = useState<number>(0);
  const [startSecond, setStartSecond] = useState<number>(0);
  const [startPeriod, setStartPeriod] = useState<string>("AM");

  const [endHour, setEndHour] = useState<number>(11);
  const [endMinute, setEndMinute] = useState<number>(59);
  const [endSecond, setEndSecond] = useState<number>(59);
  const [endPeriod, setEndPeriod] = useState<string>("PM");
  const [reRender, setReRender] = useState<boolean>(false);
  const [utc, setUtc] = useState(props.utc);
  const [mode, setMode] = useState("os");
  const [showTimeZone, setShowTimeZone] = useState(false);

  moment.locale(lang?.toLocaleLowerCase() || LANG.EN.toLocaleLowerCase());

  const timePicker = {
    start: {
      label: "from",
      hour: startHour,
      minute: startMinute,
      second: startSecond,
      period: startPeriod,
      setHour: setStartHour,
      setMinute: setStartMinute,
      setSecond: setStartSecond,
      setPeriod: setStartPeriod
    },
    end: {
      label: "to",
      hour: endHour,
      minute: endMinute,
      second: endSecond,
      period: endPeriod,
      setHour: setEndHour,
      setMinute: setEndMinute,
      setSecond: setEndSecond,
      setPeriod: setEndPeriod
    }
  };

  useEffect(() => {
    setSelectedDateTime(dateTime);
    setSelectedTempDateTime(dateTime);
  }, [dateTime]);

  const formatMinuteSecond = (minSec: number) =>
    minSec < 10 ? `0${minSec}` : minSec.toString();

  const onChangeMode = (currentMode: CalendarModeRangeType) => {
    const newDateTime = getDateTimeCalendar(
      currentMode,
      !modes?.includes(CALENDAR_MODE_RANGE_TYPE.TODAY)
        ? moment().subtract(1, "d").tz(currentTimeZone).toISOString(true)
        : moment().tz(currentTimeZone).toISOString(true),
      undefined,
      props.baseTime ? props.baseTime : undefined,
      currentTimeZone
    );
    setEndHour(11);
    setEndMinute(59);
    setEndSecond(59);
    setEndPeriod("PM");

    setStartHour(12);
    setStartMinute(0);
    setStartSecond(0);
    setStartPeriod("AM");

    setSelectedTempDateTime({
      mode: currentMode,
      ...newDateTime
    });
    setReRender(true);
    setTimeout(() => setReRender(false), 10);
  };

  const onChangeDate = (date: Date | null | [Date | null, Date | null]) => {
    if (date && Array.isArray(date)) {
      const [startDateTime, endDateTime] = date;
      let newDateTime: SearchComparisonDateTimeTemporary = {
        mode: CALENDAR_MODE_RANGE_TYPE.CUSTOM,
        start: momentTimeZone(startDateTime).tz(currentTimeZone, true).format()
      };

      if (endDateTime) {
        newDateTime = {
          ...newDateTime,
          ...getDateTimeCalendar(
            newDateTime.mode,
            momentTimeZone(startDateTime).tz(currentTimeZone, true).format(),
            momentTimeZone(endDateTime).tz(currentTimeZone, true).format(),
            undefined,
            currentTimeZone
          )
        };
      }
      setSelectedTempDateTime(newDateTime);
    }
  };

  const renderPeriod = useMemo(() => {
    const { mode, start, end } = selectedTempDateTime;

    const isSingleDateMode =
      mode === CALENDAR_MODE_RANGE_TYPE.YESTERDAY ||
      mode === CALENDAR_MODE_RANGE_TYPE.DAY_BEFORE_YESTERDAY;

    const selectedDateFormat =
      (Object.keys(FORMAT).find(
        (key) =>
          FORMAT[key as keyof typeof FORMAT].toLowerCase() ===
          dateFormat?.toLowerCase()
      ) as keyof typeof FORMAT) || "MONTH_DATE_YEAR_1";
    const localLang = lang.toLocaleLowerCase();
    const formattedStartDate = moment(start)
      .tz(currentTimeZone)
      .locale(localLang)
      .format(FORMAT[selectedDateFormat]);

    const formattedStartTime = showTimePicker
      ? `${startHour || 12}:${formatMinuteSecond(
          startMinute || 0
        )}:${formatMinuteSecond(startSecond || 0)} ${
          translate(startPeriod.toLocaleUpperCase()) || translate("am")
        }`
      : "";

    const formattedEndDate = moment(end || start)
      .tz(currentTimeZone)
      .locale(localLang)
      .format(FORMAT[selectedDateFormat]);

    const formattedEndTime = showTimePicker
      ? `${endHour || 11}:${formatMinuteSecond(
          endMinute || 0
        )}:${formatMinuteSecond(endSecond || 0)} ${
          translate(endPeriod.toLocaleUpperCase()) || translate("pm")
        }`
      : "";
    return (
      <>
        {mode === CALENDAR_MODE_RANGE_TYPE.TODAY && <div className="circle" />}
        {!noTextMode && <p className="mode">{translate(mode)}</p>}
        <div className="time-start">
          {mode === CALENDAR_MODE_RANGE_TYPE.TODAY ? (
            <span>
              {formattedStartDate}&nbsp;{currentTime}
            </span>
          ) : (
            <>
              <span>
                {formattedStartDate} {formattedStartTime}
              </span>
              {(showTimePicker || !isSingleDateMode) && <span> - </span>}
              <span>
                {isSingleDateMode
                  ? ` ${formattedEndTime}`
                  : `${
                      formattedEndDate || formattedStartDate
                    } ${formattedEndTime}`}
              </span>
            </>
          )}
          {showDropdownIcon && (
            <HVIcon
              name="arrow_down"
              scale="large"
              theme="fill"
              size={10}
              svgProps={{
                height: "10px",
                width: "10px"
              }}
            />
          )}
        </div>
      </>
    );
  }, [selectedDateTime, currentTime, currentTimeZone, lang]);

  useEffect(() => {
    return () => {
      if (intervalCurrentTime.current) {
        clearInterval(
          intervalCurrentTime.current as ReturnType<typeof setInterval>
        );
        intervalCurrentTime.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!showDatePicker && selectedTempDateTime.mode !== dateTime.mode) {
      setSelectedTempDateTime(dateTime);
    }
  }, [dateTime.mode]);

  useEffect(() => {
    if (currentTimeZone && !isConstantTimezone && isCalendar) {
      setTimeZone?.(currentTimeZone);
    }
  }, [currentTimeZone]);

  const setCurrentDateTime = (timeFormat: string, localLang: string) => {
    setCurrentTime(
      momentTimeZone
        .tz(currentTimeZone)
        .locale(localLang.toLocaleLowerCase())
        .format(timeFormat)
    );
  };

  useEffect(() => {
    const shouldRunInterval =
      dateTime.mode === CALENDAR_MODE_RANGE_TYPE.TODAY ||
      dateTime.mode === CALENDAR_MODE_RANGE_TYPE.LAST_24_HOURS;

    if (intervalCurrentTime.current) {
      clearInterval(intervalCurrentTime.current);
      intervalCurrentTime.current = null;
    }

    if (shouldRunInterval) {
      setCurrentTime(
        momentTimeZone
          .tz(currentTimeZone)
          .locale(lang.toLowerCase())
          .format(FORMAT.TIM12)
      );

      intervalCurrentTime.current = setInterval(() => {
        setCurrentDateTime(` ${FORMAT.TIME_12H_2}`, lang);
      }, 10 * 1000);
    }

    return () => {
      if (intervalCurrentTime.current) {
        clearInterval(intervalCurrentTime.current);
        intervalCurrentTime.current = null;
      }
    };
  }, [dateTime.mode, lang, currentTimeZone]);

  const formatDateForCalender = (date: string) => {
    return new Date(
      new Date(
        momentTimeZone.tz(date, currentTimeZone).toISOString(true)
      ).toLocaleString("en", {
        timeZone: currentTimeZone
      })
    );
  };

  useEffect(() => {
    let maxDate;
    if (selectedTempDateTime.mode === CALENDAR_MODE_RANGE_TYPE.CUSTOM) {
      let defaultLimit = 90;
      if (yearIsLimit) {
        defaultLimit = 365;
      }
      // The limit must be -1. Because it includes the start date
      // ex. 90 days -> 89 limit
      // ex. 365 days (1 year) -> 364 limit
      const customMaxDate = momentTimeZone
        .tz(selectedStart, currentTimeZone)
        .add(defaultLimit - 1, "d");

      maxDate = customMaxDate.isAfter(
        momentTimeZone.tz(props.baseTime, currentTimeZone),
        "day"
      )
        ? formatDateForCalender(moment(props.baseTime).toISOString())
        : customMaxDate.toDate();
    } else {
      maxDate = formatDateForCalender(moment(props.baseTime).toISOString());
    }

    setMaxDate(maxDate);
  }, [
    selectedStart,
    selectedTempDateTime.mode,
    props.baseTime,
    currentTimeZone
  ]);

  useEffect(() => {
    if (currentTimeZone !== previousTimeZone) {
      if (intervalCurrentTime.current) {
        clearInterval(
          intervalCurrentTime.current as ReturnType<typeof setInterval>
        );
        intervalCurrentTime.current = null;
      }
      if (intervalCurrentTime.current === null) {
        setCurrentTime(
          momentTimeZone
            .tz(currentTimeZone)
            .locale(lang.toLocaleLowerCase())
            .format(FORMAT.TIME_12H_2)
        );
        intervalCurrentTime.current = setInterval(
          () => setCurrentDateTime(` ${FORMAT.TIME_12H_2}`, lang),
          10 * 1000
        );
      }
      setPreviousTimeZone(currentTimeZone);
    }
  });

  const createDateTime = (
    date: Date,
    hour: number,
    minute: number,
    second: number,
    period: string
  ) => {
    let hoursIn24Format;
    if (period === "PM" && hour < 12) {
      hoursIn24Format = hour + 12;
    } else if (period === "AM" && hour === 12) {
      hoursIn24Format = 0;
    } else {
      hoursIn24Format = hour;
    }

    const dateTime = moment(date).tz(currentTimeZone);
    return dateTime
      .set({ hour: hoursIn24Format, minute, second })
      .toISOString(true);
  };

  const classNames = useMemo(() => {
    const list = ["date-range-picker"];
    if (props.disabled) {
      list.push("disabled");
    }

    if (!usePreview) {
      list.push("disable-preview-mode");
    }
    return list.join(" ");
  }, [props.disabled, usePreview]);

  return (
    <>
      <DateRangePickerContainerStyled
        id={`date-range-picker_${uniq}`}
        className={classNames}
        {...props}
      >
        <button
          className="date-range-picker-control"
          onClick={() => {
            if (props.disabled) return;
            const isShow = !showDatePicker;
            setShowDatePicker(isShow);
            props.onShow?.(isShow);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setShowDatePicker(true);
              props.onShow?.(true);
            }
          }}
          aria-label="Date range picker"
          tabIndex={tabIndex}
        >
          {showIcon ? (
            <div className="icon-calendar">
              <HVIcon name="calendar" svgProps={{ ...ICON.SIZE_16 }} />
            </div>
          ) : null}
          {!displayMode ? (
            <div className="period">{renderPeriod}</div>
          ) : (
            <div style={{ whiteSpace: "nowrap" }}>
              <p className="mode-time-range">{translate(selectedMode)}</p>
            </div>
          )}
        </button>
        {showDatePicker && (
          <DateRangePickerStyled className="date-range-popup">
            <div>
              {showRangeMode && (
                <div className="date-range-popup-sidebar">
                  <ScrollBar>
                    {Object.values(modes || CALENDAR_MODE_RANGE_TYPE)
                      .filter(
                        (item) =>
                          item !== CALENDAR_MODE_RANGE_TYPE.LAST_24_HOURS
                      )
                      .map((currentMode) => (
                        <button
                          key={currentMode}
                          aria-label={`Range ${currentMode.replaceAll(
                            "_",
                            " "
                          )} `}
                          className={
                            currentMode === selectedMode ? "active" : ""
                          }
                          onClick={() => onChangeMode(currentMode)}
                          tabIndex={tabIndex}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              onChangeMode(currentMode);
                            }
                          }}
                        >
                          {translate(currentMode)}
                        </button>
                      ))}
                  </ScrollBar>
                </div>
              )}
              <div className="date-range-calendar">
                <Calendar
                  // to re-render calendar when user change mode to get focus on that month and date
                  key={reRender.toString()}
                  inline
                  selectsRange
                  showPreviousMonths
                  focusSelectedMonth
                  today={
                    modes?.includes(CALENDAR_MODE_RANGE_TYPE.TODAY)
                      ? formatDateForCalender(
                          moment(props.baseTime).toISOString()
                        )
                      : formatDateForCalender(moment().toISOString())
                  }
                  selected={
                    selectedMode === CALENDAR_MODE_RANGE_TYPE.TODAY
                      ? formatDateForCalender(moment().toISOString())
                      : formatDateForCalender(selectedStart)
                  }
                  startDate={
                    selectedMode === CALENDAR_MODE_RANGE_TYPE.TODAY
                      ? formatDateForCalender(moment().toISOString())
                      : formatDateForCalender(selectedStart)
                  }
                  endDate={
                    selectedEnd ? formatDateForCalender(selectedEnd) : undefined
                  }
                  maxDate={maxDate}
                  onChange={onChangeDate}
                  {...(yearIsLimit
                    ? {
                        minDate: new Date(
                          momentTimeZone
                            .tz(moment(props.baseTime), currentTimeZone)
                            .subtract(CALENDAR.MAX_SELECTABLE_DAY, "days")
                            .toDate()
                        )
                      }
                    : {})}
                />
                {showTimePicker && (
                  <div className="time-range">
                    {(
                      Object.keys(timePicker) as Array<keyof typeof timePicker>
                    ).map((type) => {
                      const isTodayMode =
                        selectedMode === CALENDAR_MODE_RANGE_TYPE.TODAY;
                      const disabledClass = isTodayMode ? "disabled" : "";
                      return (
                        <div key={type} className="time-group">
                          <div className="time-label-date">
                            <div className="time-label">
                              <span>{translate(timePicker[type].label)}</span>
                            </div>
                            <div className="time-date">
                              {moment(
                                type === "start"
                                  ? selectedTempDateTime.start
                                  : selectedTempDateTime.end ||
                                      selectedTempDateTime.start
                              ).format(FORMAT.MONTH_DATE_YEAR_1)}
                            </div>
                          </div>

                          <div className="time-input-group">
                            <div className="input-arrow-container">
                              <input
                                aria-label={`${type} hour`}
                                type="number"
                                value={timePicker[type].hour}
                                className={disabledClass}
                                onChange={(e) => {
                                  const value = Number(e.target.value);
                                  timePicker[type].setHour(
                                    value >= 1 && value <= 12
                                      ? value
                                      : timePicker[type].hour
                                  );
                                }}
                                min="1"
                                max="12"
                                disabled={isTodayMode}
                              />
                              <div className="arrow-container">
                                <button
                                  className={disabledClass}
                                  onClick={() =>
                                    timePicker[type].setHour(
                                      Math.min(timePicker[type].hour + 1, 12)
                                    )
                                  }
                                  aria-label={`Increment ${type} hour`}
                                  disabled={isTodayMode}
                                >
                                  <HVIcon
                                    name="arrow-chevron-up"
                                    svgProps={{ ...ICON.SIZE_12 }}
                                  />
                                </button>
                                <button
                                  className={disabledClass}
                                  onClick={() =>
                                    timePicker[type].setHour(
                                      Math.max(timePicker[type].hour - 1, 1)
                                    )
                                  }
                                  aria-label={`Decrement ${type} hour`}
                                  disabled={isTodayMode}
                                >
                                  <HVIcon
                                    name="arrow-chevron-down"
                                    svgProps={{ ...ICON.SIZE_12 }}
                                  />
                                </button>
                              </div>
                            </div>
                            <span>:</span>
                            <div className="input-arrow-container">
                              <input
                                aria-label={`${type} minute`}
                                className={disabledClass}
                                type="number"
                                value={formatMinuteSecond(
                                  timePicker[type].minute
                                )}
                                onChange={(e) => {
                                  const value = Number(e.target.value);
                                  timePicker[type].setMinute(
                                    value >= 0 && value <= 59
                                      ? value
                                      : timePicker[type].minute
                                  );
                                }}
                                min="0"
                                max="59"
                                disabled={isTodayMode}
                              />
                              <div className="arrow-container">
                                <button
                                  className={disabledClass}
                                  onClick={() =>
                                    timePicker[type].setMinute(
                                      Math.min(timePicker[type].minute + 1, 59)
                                    )
                                  }
                                  aria-label={`Increment ${type} minute`}
                                  disabled={isTodayMode}
                                >
                                  <HVIcon
                                    name="arrow-chevron-up"
                                    svgProps={{ ...ICON.SIZE_12 }}
                                  />
                                </button>
                                <button
                                  className={disabledClass}
                                  onClick={() =>
                                    timePicker[type].setMinute(
                                      Math.max(timePicker[type].minute - 1, 0)
                                    )
                                  }
                                  aria-label={`Decrement ${type} minute`}
                                  disabled={isTodayMode}
                                >
                                  <HVIcon
                                    name="arrow-chevron-down"
                                    svgProps={{ ...ICON.SIZE_12 }}
                                  />
                                </button>
                              </div>
                            </div>
                            <span>:</span>
                            <div className="input-arrow-container">
                              <input
                                aria-label={`${type} second`}
                                className={disabledClass}
                                type="number"
                                value={formatMinuteSecond(
                                  timePicker[type].second
                                )}
                                onChange={(e) => {
                                  const value = Number(e.target.value);

                                  timePicker[type].setSecond(
                                    value >= 0 && value <= 59
                                      ? value
                                      : timePicker[type].second
                                  );
                                }}
                                min="0"
                                max="59"
                                disabled={isTodayMode}
                              />
                              <div className="arrow-container">
                                <button
                                  className={disabledClass}
                                  onClick={() =>
                                    timePicker[type].setSecond(
                                      Math.min(timePicker[type].second + 1, 59)
                                    )
                                  }
                                  aria-label={`Increment ${type} second`}
                                  disabled={isTodayMode}
                                >
                                  <HVIcon
                                    name="arrow-chevron-up"
                                    svgProps={{ ...ICON.SIZE_12 }}
                                  />
                                </button>
                                <button
                                  className={disabledClass}
                                  onClick={() =>
                                    timePicker[type].setSecond(
                                      Math.max(timePicker[type].second - 1, 0)
                                    )
                                  }
                                  aria-label={`Decrement ${type} second`}
                                  disabled={isTodayMode}
                                >
                                  <HVIcon
                                    name="arrow-chevron-down"
                                    svgProps={{ ...ICON.SIZE_12 }}
                                  />
                                </button>
                              </div>
                            </div>

                            <div className="am-pm-toggle">
                              <button
                                className={`toggle-button ${
                                  timePicker[type].period === "AM" && "active"
                                }`}
                                onClick={() => timePicker[type].setPeriod("AM")}
                                aria-label={`${type} AM`}
                                disabled={isTodayMode}
                              >
                                {translate("am")}
                              </button>
                              <button
                                className={`toggle-button ${
                                  timePicker[type].period === "PM" && "active"
                                }`}
                                onClick={() => timePicker[type].setPeriod("PM")}
                                aria-label={`${type} PM`}
                                disabled={isTodayMode}
                              >
                                {translate("pm")}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <ButtonSubmit showUtc={utc}>
                  {utc && (
                    <>
                      <HVButton
                        variant="Ghost"
                        styles={{
                          root: {
                            paddingLeft: 8,
                            paddingRight: 8,
                            width: "143px"
                          }
                        }}
                        onClick={() => setShowTimeZone(!showTimeZone)}
                      >
                        <IconTimeZone />
                        <HVText
                          type="Body-sm"
                          c="var(--hv-text-normal-secondary)"
                          styles={{
                            root: {
                              paddingLeft: 4,
                              paddingRight: 4
                            }
                          }}
                        >
                          {utc}
                        </HVText>
                        {!showTimeZone ? (
                          <HVIcon
                            name="arrow_chevron_down"
                            svgProps={{ ...ICON.SIZE_20 }}
                          />
                        ) : (
                          <HVIcon
                            name="arrow_chevron_up"
                            svgProps={{ ...ICON.SIZE_20 }}
                          />
                        )}
                      </HVButton>
                      {modes?.includes(CALENDAR_MODE_RANGE_TYPE.TODAY) && (
                        <HVButton
                          variant="Ghost"
                          styles={{
                            root: {
                              paddingLeft: 0,
                              paddingRight: 0,
                              width: "80px"
                            }
                          }}
                          onClick={() => onChangeMode("today")}
                        >
                          <HVText
                            type="Body-sm"
                            c="var(--hv-text-normal-secondary)"
                            title={translate(dateTime.mode)}
                            styles={{
                              root: {
                                width: "80px"
                              }
                            }}
                          >
                            {translate("today")}
                          </HVText>
                        </HVButton>
                      )}
                    </>
                  )}
                  <HVButton
                    variant="Primary"
                    aria-label="date-range-picker-submit-button"
                    onClick={() => {
                      const { mode, start, end } = selectedTempDateTime;
                      const dateTime = getDateTime(mode, start, end);

                      const startDateTime = createDateTime(
                        momentTimeZone
                          .tz(start, currentTimeZone)
                          .startOf("day")
                          .toDate(),
                        startHour,
                        startMinute,
                        startSecond,
                        startPeriod
                      );
                      const endDateTime = createDateTime(
                        end
                          ? momentTimeZone
                              .tz(end, currentTimeZone)
                              .endOf("day")
                              .toDate()
                          : momentTimeZone
                              .tz(start, currentTimeZone)
                              .endOf("day")
                              .toDate(),
                        endHour,
                        endMinute,
                        endSecond,
                        endPeriod
                      );
                      const isSameDay = moment(startDateTime).isSame(
                        endDateTime,
                        "day"
                      );
                      const periodElement =
                        document.querySelector(".am-pm-toggle");
                      if (
                        isSameDay &&
                        moment(endDateTime).isBefore(startDateTime)
                      ) {
                        if (periodElement) {
                          periodElement.classList.add("error-border");
                        }
                        return;
                      }

                      setSelectedDateTime({
                        ...selectedTempDateTime,
                        ...dateTime,
                        start: startDateTime,
                        end: endDateTime
                      });

                      !isConstantTimezone &&
                        !isCalendar &&
                        setTimeZone?.(currentTimeZone);

                      setDateTime({
                        ...selectedTempDateTime,
                        ...dateTime,
                        start: startDateTime,
                        end: endDateTime,
                        mode
                      } as T);

                      setShowDatePicker(false);
                      props.onShow?.(false);
                    }}
                  >
                    {translate("ok")}
                  </HVButton>
                </ButtonSubmit>
              </div>
            </div>
          </DateRangePickerStyled>
        )}
      </DateRangePickerContainerStyled>
      {showTimeZone && utc && (
        <TimeZone
          mode={mode}
          setMode={setMode}
          setUtc={setUtc}
          setCurrentTimeZone={setCurrentTimeZone}
          timeZone={currentTimeZone}
          currentUserTimeZone={currentUserTimeZone}
          storeSession={isCalendar}
        />
      )}
    </>
  );
};
